import { Alert, Form, Modal } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import { ResolvedSelect } from "../../../components/Select/ResolvedSelect.tsx"
import { UNRESOLVED_USER_LABEL } from "../../../utils/select/entity-labels.ts"

interface AssignProjectManagerModalProps {
  projectManagers: BaseOptionType[]
  defaultSelectedId?: string
  loading: boolean
  /** Why the PM is being asked for, shown above the picker. */
  description?: ReactNode
  onConfirm: (responsibleId: string) => void
  onCancel: () => void
}

export function AssignProjectManagerModal({
  projectManagers,
  loading,
  defaultSelectedId,
  description,
  onConfirm,
  onCancel
}: AssignProjectManagerModalProps) {
  const { t } = useTranslation()
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    defaultSelectedId || null
  )
  // Enter picks the highlighted option while the list is open; only once it
  // is closed does Enter confirm.
  const [listOpen, setListOpen] = useState(false)

  const handleConfirm = () => {
    if (selectedUserId && !loading) {
      onConfirm(selectedUserId)
    }
  }

  // Capture phase, and stopped there: the select's own Enter handler would
  // otherwise reopen the list over a modal that is already submitting.
  const handleKeyDownCapture = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey && !listOpen) {
      event.preventDefault()
      event.stopPropagation()
      handleConfirm()
    }
  }

  return (
    <Modal
      open
      title={t("assignProjectManager.title")}
      centered
      okText={t("assignProjectManager.confirm")}
      cancelText={t("assignProjectManager.cancel")}
      cancelButtonProps={{ disabled: loading }}
      okButtonProps={{
        disabled: !selectedUserId,
        loading
      }}
      onOk={handleConfirm}
      onCancel={onCancel}
    >
      {description ? (
        <Alert className="mb-4" type="info" showIcon message={description} />
      ) : null}
      <Form layout="vertical" onKeyDownCapture={handleKeyDownCapture}>
        <Form.Item label={t("assignProjectManager.label")} required>
          <ResolvedSelect
            showSearch
            placeholder={t("assignProjectManager.placeholder")}
            value={selectedUserId}
            onChange={setSelectedUserId}
            onOpenChange={setListOpen}
            optionFilterProp="label"
            className="w-full"
            options={projectManagers}
            fallbackLabel={UNRESOLVED_USER_LABEL}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
